import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import type {
  ArchivePreview,
  CitySuggestion,
  ClientCard,
  ClientCheck,
  ClientList,
  ClientListItem,
  EntityKind,
  FeedPage,
  GlobalSearch,
  JournalItem,
  MergePreview,
  ModuleFlags,
  RosterRow,
  StudioCard,
  StudioList,
  TeamCard,
  TeamList,
} from './base-types';
import { api } from './client';

/**
 * Серверное состояние M2 «База».
 *
 * Все ключи — под ['base', …], и любая правка инвалидирует весь
 * префикс: связь в одной карточке меняет роли, счётчики и ленты
 * в других, а объёмы крошечные. Перечитать дешевле, чем угадать,
 * что именно задето, — так же M1 перечитывает карточку целиком.
 */

const ROOT = 'base';

/** `?a=1&b=2` без пустых значений */
export function qs(params: Record<string, string | number | null | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (!entries.length) return '';
  return `?${entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&')}`;
}

const PLURAL: Record<EntityKind, string> = { client: 'clients', studio: 'studios', team: 'teams' };
export const entityPath = (entity: EntityKind, id: string) => `/${PLURAL[entity]}/${id}`;

// ─── общее ───

/** Флаги будущих модулей (ТЗ M2 7.3): читаются один раз за сессию. */
export const useModules = () =>
  useQuery({
    queryKey: ['config'],
    queryFn: () => api.get<{ modules: ModuleFlags }>('/config'),
    staleTime: Infinity,
    select: (r) => r.modules,
  });

export const useUsers = () =>
  useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ id: string; fullName: string }[]>('/users'),
    staleTime: 5 * 60 * 1000,
  });

export const useGlobalSearch = (q: string) =>
  useQuery({
    queryKey: [ROOT, 'search', q],
    queryFn: () => api.get<GlobalSearch>(`/search${qs({ q })}`),
    enabled: q.trim().length >= 2,
    placeholderData: keepPreviousData,
  });

export const citySuggest = (q: string) => api.get<CitySuggestion[]>(`/cities/suggest${qs({ q })}`);

// ─── списки ───

export type ListParams = Record<string, string | number | null | undefined>;

export const useClientList = (params: ListParams) =>
  useQuery({
    queryKey: [ROOT, 'clients', params],
    queryFn: () => api.get<ClientList>(`/clients${qs(params)}`),
    placeholderData: keepPreviousData,
  });

export const useStudioList = (params: ListParams, enabled = true) =>
  useQuery({
    queryKey: [ROOT, 'studios', params],
    queryFn: () => api.get<StudioList>(`/studios${qs(params)}`),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useTeamList = (params: ListParams, enabled = true) =>
  useQuery({
    queryKey: [ROOT, 'teams', params],
    queryFn: () => api.get<TeamList>(`/teams${qs(params)}`),
    placeholderData: keepPreviousData,
    enabled,
  });

/** «Найти в базе» в диалогах: до пяти активных клиентов (ТЗ M2 §8) */
export const useClientPick = (q: string, exclude: string[] = []) =>
  useQuery({
    queryKey: [ROOT, 'pick', q, exclude],
    queryFn: () => api.get<ClientListItem[]>(`/clients/pick${qs({ q, exclude: exclude.join(',') })}`),
    enabled: q.trim().length >= 2,
    placeholderData: keepPreviousData,
  });

export const useClientCheck = (params: {
  phone?: string;
  lastName?: string;
  firstName?: string;
  birthDate?: string | null;
  exclude?: string;
}) =>
  useQuery({
    queryKey: [ROOT, 'check', params],
    queryFn: () => api.get<ClientCheck>(`/clients/check${qs(params)}`),
    enabled: Boolean(params.phone?.trim() || (params.lastName && params.firstName && params.birthDate)),
    placeholderData: keepPreviousData,
  });

// ─── карточки ───

export const useClient = (id: string | undefined) =>
  useQuery({
    queryKey: [ROOT, 'client', id],
    queryFn: () => api.get<ClientCard>(`/clients/${id}`),
    enabled: Boolean(id),
  });

export const useStudio = (id: string | undefined) =>
  useQuery({
    queryKey: [ROOT, 'studio', id],
    queryFn: () => api.get<StudioCard>(`/studios/${id}`),
    enabled: Boolean(id),
  });

export const useTeam = (id: string | undefined) =>
  useQuery({
    queryKey: [ROOT, 'team', id],
    queryFn: () => api.get<TeamCard>(`/teams/${id}`),
    enabled: Boolean(id),
  });

export const useTeamMembers = (id: string | undefined) =>
  useQuery({
    queryKey: [ROOT, 'members', id],
    queryFn: () => api.get<{ active: RosterRow[]; left: RosterRow[] }>(`/teams/${id}/members`),
    enabled: Boolean(id),
  });

export const useFeed = (entity: EntityKind, id: string) =>
  useInfiniteQuery({
    queryKey: [ROOT, 'feed', entity, id],
    queryFn: ({ pageParam }) => api.get<FeedPage>(`${entityPath(entity, id)}/feed${qs({ before: pageParam })}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
  });

export const useJournal = (entity: EntityKind, id: string) =>
  useInfiniteQuery({
    queryKey: [ROOT, 'journal', entity, id],
    queryFn: ({ pageParam }) =>
      api.get<{ items: JournalItem[]; next: string | null }>(
        `/journal${qs({ entity, entityId: id, before: pageParam })}`,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
  });

export const useArchivePreview = (entity: EntityKind, id: string) =>
  useQuery({
    queryKey: [ROOT, 'archive-preview', entity, id],
    queryFn: () => api.get<ArchivePreview>(`${entityPath(entity, id)}/archive-preview`),
  });

export const useMergePreview = (entity: EntityKind, id: string, otherId: string | null) =>
  useQuery({
    queryKey: [ROOT, 'merge-preview', entity, id, otherId],
    queryFn: () => api.get<MergePreview>(`${entityPath(entity, id)}/merge-preview${qs({ otherId })}`),
    enabled: Boolean(otherId),
  });

// ─── правка ───

/**
 * Любая правка M2 перечитывает базу целиком (см. выше). Ошибку
 * показывает вызывающий — текстом сервера, как есть (ТЗ §5).
 */
export function useBaseMutation<TArgs, TResult>(
  fn: (args: TArgs) => Promise<TResult>,
  options?: Omit<UseMutationOptions<TResult, Error, TArgs>, 'mutationFn'>,
) {
  const client = useQueryClient();
  return useMutation<TResult, Error, TArgs>({
    mutationFn: fn,
    ...options,
    onSuccess: async (data, vars, ctx, mutation) => {
      await client.invalidateQueries({ queryKey: [ROOT] });
      await options?.onSuccess?.(data, vars, ctx, mutation);
    },
  });
}
